import { addDoc, collection, doc, getDocs, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import { PLATFORM_COLLECTIONS } from './platformSchema';

export function normalizeNotification(record = {}, fallbackId = '') {
  const id = record.id || record.notificationId || fallbackId || '';

  return {
    id,
    notificationId: id,
    userId: record.userId || '',
    organizationId: record.organizationId || '',
    type: record.type || 'generic',
    title: record.title || 'Notification',
    message: record.message || '',
    link: record.link || '',
    read: Boolean(record.read),
    createdAt: record.createdAt || null,
    actorUserId: record.actorUserId || '',
    actorName: record.actorName || '',
    relatedEntityType: record.relatedEntityType || '',
    relatedEntityId: record.relatedEntityId || '',
    metadata: record.metadata || {},
    deliveryChannels: Array.isArray(record.deliveryChannels) ? record.deliveryChannels : ['in_app'],
  };
}

export function getNotificationPresentation(notification = {}) {
  switch (notification.type) {
    case 'feedback_ready':
      return {
        title: notification.title || 'Feedback is ready',
        message:
          notification.message ||
          'Your instructor has finished a review and your feedback is ready to open.',
        ctaLabel: 'Open full review',
      };
    case 'organization_invite_created':
      return {
        title: notification.title || 'Organization invite',
        message: notification.message || 'An organization invite is ready for you to review.',
        ctaLabel: 'Open invite',
      };
    case 'organization_invite_accepted':
      return {
        title: notification.title || 'Invite accepted',
        message: notification.message || 'An invited user accepted your organization invite.',
        ctaLabel: 'Open organization workspace',
      };
    case 'organization_invite_declined':
      return {
        title: notification.title || 'Invite declined',
        message: notification.message || 'An invited user declined your organization invite.',
        ctaLabel: 'Open organization workspace',
      };
    case 'lesson_assigned':
      return {
        title: notification.title || 'Lesson assigned',
        message: notification.message || 'A lesson has been assigned to you.',
        ctaLabel: 'Open dashboard',
      };
    case 'video_assigned':
      return {
        title: notification.title || 'Video assigned',
        message: notification.message || 'A video has been assigned to you for review.',
        ctaLabel: 'Open dashboard',
      };
    case 'entitlement_granted':
      return {
        title: notification.title || 'Premium content unlocked',
        message: notification.message || 'Your organization now has access to a content pack.',
        ctaLabel: 'Open organization workspace',
      };
    case 'entitlement_revoked':
      return {
        title: notification.title || 'Entitlement updated',
        message: notification.message || 'A content pack entitlement was revoked for your organization.',
        ctaLabel: 'Open organization workspace',
      };
    case 'premium_content_unlocked':
      return {
        title: notification.title || 'Premium content unlocked',
        message: notification.message || 'Premium drills are now available to your organization.',
        ctaLabel: 'Open organization workspace',
      };
    case 'organization_upgrade_requested':
      return {
        title: notification.title || 'Upgrade requested',
        message: notification.message || 'An upgrade request was submitted for your organization.',
        ctaLabel: 'Open organization workspace',
      };
    case 'organization_trial_requested':
      return {
        title: notification.title || 'Trial requested',
        message: notification.message || 'A trial request was submitted for your organization.',
        ctaLabel: 'Open organization workspace',
      };
    case 'booking_request_created':
      return {
        title: notification.title || 'Booking request received',
        message: notification.message || 'A new booking request is waiting for review.',
        ctaLabel: 'Open dashboard',
      };
    case 'booking_request_accepted':
      return {
        title: notification.title || 'Booking request accepted',
        message: notification.message || 'Your booking request was accepted.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_request_declined':
      return {
        title: notification.title || 'Booking request declined',
        message: notification.message || 'Your booking request was declined.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_request_canceled':
      return {
        title: notification.title || 'Booking request canceled',
        message: notification.message || 'A booking request was canceled.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_request_completed':
      return {
        title: notification.title || 'Booking completed',
        message: notification.message || 'A booking was marked complete.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_confirmed':
      return {
        title: notification.title || 'Booking confirmed',
        message: notification.message || 'Your booking has been confirmed.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_rescheduled':
      return {
        title: notification.title || 'Booking rescheduled',
        message: notification.message || 'A booking time was updated.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_canceled':
      return {
        title: notification.title || 'Booking canceled',
        message: notification.message || 'A booking was canceled.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_completed':
      return {
        title: notification.title || 'Booking completed',
        message: notification.message || 'A booking was marked complete.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_upcoming_reminder':
      return {
        title: notification.title || 'Upcoming session reminder',
        message: notification.message || 'A booking is coming up soon.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_post_session_upload':
      return {
        title: notification.title || 'Upload session video',
        message: notification.message || 'Your session is complete. Upload video or notes to continue coaching.',
        ctaLabel: 'Open upload',
      };
    case 'booking_feedback_ready':
      return {
        title: notification.title || 'Session feedback ready',
        message: notification.message || 'Session feedback is ready to review.',
        ctaLabel: 'Open full review',
      };
    case 'booking_rebook_recommended':
      return {
        title: notification.title || 'Book your next session',
        message: notification.message || 'It may be time to schedule your next session.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_no_show':
      return {
        title: notification.title || 'Booking marked no-show',
        message: notification.message || 'A booking was marked as a no-show.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_follow_up_requested':
      return {
        title: notification.title || 'Session follow-up requested',
        message: notification.message || 'A session follow-up is ready for you.',
        ctaLabel: 'Open upload',
      };
    case 'booking_change_requested':
      return {
        title: notification.title || 'Booking change requested',
        message: notification.message || 'A booking change request is waiting for review.',
        ctaLabel: 'Open bookings',
      };
    case 'booking_change_approved':
      return {
        title: notification.title || 'Booking change approved',
        message: notification.message || 'Your booking change request was approved.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_change_declined':
      return {
        title: notification.title || 'Booking change declined',
        message: notification.message || 'Your booking change request was declined.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_payment_pending':
      return {
        title: notification.title || 'Booking payment pending',
        message: notification.message || 'This booking may require payment or deposit later.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_payment_received':
      return {
        title: notification.title || 'Payment received',
        message: notification.message || 'Payment for your booking was received.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_payment_failed':
      return {
        title: notification.title || 'Payment failed',
        message: notification.message || 'A booking payment failed and may need attention.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_refund_issued':
      return {
        title: notification.title || 'Refund issued',
        message: notification.message || 'A refund was issued for your booking.',
        ctaLabel: 'Open my bookings',
      };
    case 'booking_coverage_applied':
      return {
        title: notification.title || 'Coverage applied',
        message: notification.message || 'Package or plan coverage was applied to your booking.',
        ctaLabel: 'Open my bookings',
      };
    case 'package_assigned':
      return {
        title: notification.title || 'Package assigned',
        message: notification.message || 'A new service package or membership was assigned to you.',
        ctaLabel: 'Open my bookings',
      };
    case 'package_credits_low':
      return {
        title: notification.title || 'Coverage running low',
        message: notification.message || 'Your remaining sessions or credits are getting low.',
        ctaLabel: 'Open my bookings',
      };
    case 'membership_expiring_soon':
      return {
        title: notification.title || 'Membership expiring soon',
        message: notification.message || 'One of your memberships or packages is expiring soon.',
        ctaLabel: 'Open my bookings',
      };
    case 'package_coverage_exhausted':
      return {
        title: notification.title || 'Coverage exhausted',
        message: notification.message || 'Your package or membership coverage has been used up.',
        ctaLabel: 'Open my bookings',
      };

    // ── Coaching / AI notifications (Pass 40) ──────────────
    case 'video_uploaded':
      return {
        title: notification.title || 'Video uploaded successfully',
        message: notification.message || 'Your video is now waiting for coach review.',
        ctaLabel: 'View my videos',
      };
    case 'new_message':
      return {
        title: notification.title || 'New message',
        message: notification.message || 'You have a new message waiting.',
        ctaLabel: 'Open messages',
      };
    case 'lesson_plan_ready':
      return {
        title: notification.title || 'New lesson plan from your coach',
        message: notification.message || 'Your coach created a new lesson plan for you.',
        ctaLabel: 'View lesson plan',
      };
    case 'review_claimed':
      return {
        title: notification.title || 'A coach is reviewing your video',
        message: notification.message || 'Your video has been claimed by an instructor.',
        ctaLabel: 'View my videos',
      };
    case 'ai_notes_ready':
      return {
        title: notification.title || 'AI coaching notes ready',
        message: notification.message || 'AI-generated coaching analysis is available for your video.',
        ctaLabel: 'View feedback',
      };
    case 'progress_unlocked':
      return {
        title: notification.title || 'Progress milestone unlocked!',
        message: notification.message || 'You reached a new level in your coaching journey.',
        ctaLabel: 'View progress',
      };
    case 'drill_assigned':
      return {
        title: notification.title || 'New drill assigned',
        message: notification.message || 'Your coach assigned you a new drill to practice.',
        ctaLabel: 'View drills',
      };
    case 'session_summary_complete':
      return {
        title: notification.title || 'Session summary complete',
        message: notification.message || 'Your coach completed a session summary with feedback.',
        ctaLabel: 'View progress',
      };

    default:
      return {
        title: notification.title || 'Notification',
        message: notification.message || 'Open to view details.',
        ctaLabel: 'Open related page',
      };
  }
}

// Dedup cache: prevent duplicate notifications for the same event within 30 seconds.
// Key = `${userId}:${type}:${relatedEntityId}`, value = timestamp.
const _recentNotifications = new Map();
const DEDUP_WINDOW_MS = 30_000;

export async function createNotification(
  db,
  {
    userId = '',
    organizationId = '',
    // Pass 85.1B.3 (§10) — EXPLICIT notification audience. '' (default) = legacy/unclassified → NOT staff-readable;
    // 'organization_staff' = operational (readable by a current org-comm-role recipient); 'commercial'/'invitee'/'account'
    // = source-backed recipient relationships that remain FAIL-CLOSED for the staff branch (no source-backed read yet).
    audience = '',
    type = 'generic',
    title = '',
    message = '',
    link = '',
    actorUserId = '',
    actorName = '',
    relatedEntityType = '',
    relatedEntityId = '',
    metadata = {},
    deliveryChannels = ['in_app'],
    // When provided, the notification is written to a DETERMINISTIC document id
    // via setDoc. Re-processing the same logical event (same id) overwrites in
    // place instead of creating a duplicate — a stronger guarantee than the
    // time-window guard, and robust to retries / delayed duplicate writes.
    notificationId = '',
  } = {}
) {
  if (!userId) return null;

  const payload = {
    userId,
    organizationId,
    ...(audience ? { audience } : {}),
    type,
    title,
    message,
    link,
    read: false,
    createdAt: serverTimestamp(),
    actorUserId,
    actorName,
    relatedEntityType,
    relatedEntityId,
    metadata,
    deliveryChannels,
  };

  // Deterministic-id path: idempotent, no time window needed.
  if (notificationId) {
    await setDoc(doc(db, PLATFORM_COLLECTIONS.NOTIFICATIONS, notificationId), payload, { merge: true });
    return normalizeNotification({ id: notificationId, ...payload });
  }

  // Legacy auto-id path retains the 30s in-memory dedup guard.
  const dedupKey = `${userId}:${type}:${relatedEntityId || 'none'}`;
  const lastCreated = _recentNotifications.get(dedupKey);
  if (lastCreated && Date.now() - lastCreated < DEDUP_WINDOW_MS) {
    return null; // Skip duplicate
  }
  _recentNotifications.set(dedupKey, Date.now());
  if (_recentNotifications.size > 100) {
    const cutoff = Date.now() - DEDUP_WINDOW_MS;
    for (const [key, ts] of _recentNotifications) {
      if (ts < cutoff) _recentNotifications.delete(key);
    }
  }

  const docRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.NOTIFICATIONS), payload);
  return normalizeNotification({ id: docRef?.id || '', ...payload });
}

// Pass 75 (§10/§11) — CREATE-ONCE notification for an idempotent LOGICAL delivery event (feedback_ready,
// new_message). Unlike createNotification's deterministic-id path (which re-merges `read:false` + a fresh
// `createdAt` on every retry — visually resurrecting an already-read notification), this creates the doc ONLY
// when it does not already exist, in a transaction, so a recovery/retry NEVER resets the recipient's `read`
// state, `createdAt`, recipient, or original event identity. Requires a deterministic `notificationId`.
export async function createNotificationOnce(
  db,
  {
    notificationId = '',
    userId = '',
    organizationId = '',
    participantId = '', // Pass 85.1B — canonical student tenancy; when set, the student reads via participantSelfByProfile
    type = 'generic',
    title = '',
    message = '',
    link = '',
    actorUserId = '',
    actorName = '',
    relatedEntityType = '',
    relatedEntityId = '',
    metadata = {},
    deliveryChannels = ['in_app'],
  } = {}
) {
  if (!notificationId || !userId) return null;
  const ref = doc(db, PLATFORM_COLLECTIONS.NOTIFICATIONS, notificationId);
  // Pass 75 SECURITY REPAIR — a canonical create-once delivery FAILURE must be OBSERVABLE by the caller. This
  // used to catch the transaction error and return null, so a deterministic notification write that failed
  // (rules deny, network) was silently reported as delivery success and never recovered. It now logs and
  // RE-THROWS: deliverReviewedVideo / sendThreadMessage reject, the video stays `reviewed` (already persisted),
  // the UI keeps the same stable id, and an idempotent retry restores ONLY the missing artifact (existing
  // thread/intro/message are untouched — never reordered, read/createdAt never reset).
  try {
    const outcome = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) return { existed: true }; // idempotent recovery — do NOT touch read/createdAt
      tx.set(ref, {
        userId, organizationId, ...(participantId ? { participantId } : {}), type, title, message, link,
        read: false, createdAt: serverTimestamp(),
        actorUserId, actorName, relatedEntityType, relatedEntityId, metadata, deliveryChannels,
      });
      return { existed: false };
    });
    return { id: notificationId, existed: outcome.existed };
  } catch (err) {
    console.warn('createNotificationOnce failed:', err);
    throw err;
  }
}

export async function createNotificationsForUsers(db, userIds = [], payload = {}) {
  const uniqueUserIds = Array.from(new Set((userIds || []).filter(Boolean)));
  const notifications = await Promise.all(
    uniqueUserIds.map((userId) => createNotification(db, { ...payload, userId }))
  );
  return notifications.filter(Boolean);
}

export async function createOrganizationNotification(
  db,
  {
    organizationId = '',
    roles = [],
    excludeUserIds = [],
    ...payload
  } = {}
) {
  if (!organizationId) return [];

  const membershipRecords = (await loadRecordsByEquality(db, {
    collectionName: 'organizationMembers',
    field: 'organizationId',
    value: organizationId,
  })) || [];

  const roleSet = new Set((roles || []).filter(Boolean));
  const excluded = new Set((excludeUserIds || []).filter(Boolean));

  const targetUserIds = membershipRecords
    .filter((membership) => membership.status !== 'inactive' && membership.status !== 'revoked')
    .filter((membership) => !roleSet.size || roleSet.has(membership.role || membership.orgRole))
    .map((membership) => membership.userId)
    .filter((userId) => userId && !excluded.has(userId));

  return createNotificationsForUsers(db, targetUserIds, {
    // Pass 85.1B.3 — org-role-targeted notifications are OPERATIONAL by construction → explicit organization_staff audience
    // (a caller may override by passing an explicit audience in payload).
    audience: 'organization_staff',
    organizationId,
    ...payload,
  });
}

export async function getUserNotifications(db, userId = '', { unreadOnly = false } = {}) {
  if (!userId) return [];

  const collectionRef = collection(db, PLATFORM_COLLECTIONS.NOTIFICATIONS);
  const constraints = [where('userId', '==', userId)];

  if (unreadOnly) {
    constraints.push(where('read', '==', false));
  }

  constraints.push(orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(query(collectionRef, ...constraints));
  return snapshot.docs.map((docSnap) => normalizeNotification({ id: docSnap.id, ...docSnap.data() }));
}

export async function getUnreadNotificationCount(db, userId = '') {
  const notifications = await getUserNotifications(db, userId, { unreadOnly: true });
  return notifications.length;
}

export async function markNotificationRead(db, notificationId = '') {
  if (!notificationId) return;

  await updateDoc(doc(db, PLATFORM_COLLECTIONS.NOTIFICATIONS, notificationId), {
    read: true,
  });
}

export async function markAllNotificationsRead(db, userId = '') {
  const notifications = await getUserNotifications(db, userId, { unreadOnly: true });
  await Promise.all(
    notifications.map((notification) => markNotificationRead(db, notification.notificationId))
  );
}

export async function notifyOrganizationInviteCreated(db, invite = {}) {
  if (!invite?.linkedUserId) return null;

  return createNotification(db, {
    userId: invite.linkedUserId,
    organizationId: invite.organizationId,
    audience: 'invitee',
    type: 'organization_invite_created',
    title: `Invitation to join ${invite.organizationName || invite.organizationId}`,
    message: `You were invited to join ${invite.organizationName || invite.organizationId} as ${invite.role}.`,
    link: `/invite/${invite.inviteCode}`,
    actorUserId: invite.invitedByUserId,
    actorName: invite.invitedByName,
    relatedEntityType: 'organization_invite',
    relatedEntityId: invite.inviteId,
    metadata: {
      role: invite.role,
    },
  });
}

export async function notifyOrganizationInviteAccepted(db, invite = {}, membership = {}) {
  if (!invite?.invitedByUserId) return null;

  return createNotification(db, {
    userId: invite.invitedByUserId,
    organizationId: invite.organizationId,
    audience: 'organization_staff',
    type: 'organization_invite_accepted',
    title: 'Organization invite accepted',
    message: `${membership.name || invite.email || 'A user'} accepted the invite to join ${invite.organizationName || invite.organizationId}.`,
    link: '/organization',
    actorUserId: invite.linkedUserId,
    actorName: membership.name || '',
    relatedEntityType: 'organization_invite',
    relatedEntityId: invite.inviteId,
  });
}

export async function notifyOrganizationInviteDeclined(db, invite = {}) {
  if (!invite?.invitedByUserId) return null;

  return createNotification(db, {
    userId: invite.invitedByUserId,
    organizationId: invite.organizationId,
    audience: 'organization_staff',
    type: 'organization_invite_declined',
    title: 'Organization invite declined',
    message: `${invite.email || 'An invited user'} declined their invite to join ${invite.organizationName || invite.organizationId}.`,
    link: '/organization',
    actorUserId: invite.linkedUserId,
    actorName: '',
    relatedEntityType: 'organization_invite',
    relatedEntityId: invite.inviteId,
  });
}

export async function notifyInstructorAssignedLesson(
  db,
  {
    instructorId = '',
    organizationId = '',
    lessonId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  return createNotification(db, {
    userId: instructorId,
    organizationId,
    audience: 'organization_staff',
    type: 'lesson_assigned',
    title: 'Lesson assigned',
    message: 'A lesson was assigned to you.',
    link: '/instructor',
    actorUserId,
    actorName,
    relatedEntityType: 'lesson_instance',
    relatedEntityId: lessonId,
  });
}

export async function notifyInstructorAssignedVideo(
  db,
  {
    instructorId = '',
    organizationId = '',
    videoId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  return createNotification(db, {
    userId: instructorId,
    organizationId,
    audience: 'organization_staff',
    type: 'video_assigned',
    title: 'Video assigned',
    message: 'A review video was assigned to you.',
    link: '/instructor',
    actorUserId,
    actorName,
    relatedEntityType: 'video',
    relatedEntityId: videoId,
  });
}

export async function notifyEntitlementGranted(
  db,
  {
    organizationId = '',
    contentPackId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  await createOrganizationNotification(db, {
    organizationId,
    type: 'entitlement_granted',
    title: 'Entitlement granted',
    message: `Your organization was granted access to content pack ${contentPackId}.`,
    link: '/organization',
    actorUserId,
    actorName,
    relatedEntityType: 'content_pack',
    relatedEntityId: contentPackId,
  });

  return createOrganizationNotification(db, {
    organizationId,
    type: 'premium_content_unlocked',
    title: 'Premium content unlocked',
    message: `Premium content pack ${contentPackId} is now available to your organization.`,
    link: '/organization',
    actorUserId,
    actorName,
    relatedEntityType: 'content_pack',
    relatedEntityId: contentPackId,
  });
}

export async function notifyEntitlementRevoked(
  db,
  {
    organizationId = '',
    contentPackId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  return createOrganizationNotification(db, {
    organizationId,
    type: 'entitlement_revoked',
    title: 'Entitlement revoked',
    message: `Access to content pack ${contentPackId} was revoked for your organization.`,
    link: '/organization',
    actorUserId,
    actorName,
    relatedEntityType: 'content_pack',
    relatedEntityId: contentPackId,
  });
}

export async function notifyBookingRequestCreated(
  db,
  {
    bookingRequest = {},
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const targetRoles = bookingRequest.instructorId ? ['organization_owner', 'organization_admin', 'instructor'] : ['organization_owner', 'organization_admin'];

  return createOrganizationNotification(db, {
    organizationId: bookingRequest.organizationId,
    roles: targetRoles,
    type: 'booking_request_created',
    title: 'Booking request received',
    message: `${bookingRequest.requesterName || bookingRequest.requesterEmail || 'A prospective client'} requested ${bookingRequest.serviceType || 'a session'}.`,
    link: bookingRequest.instructorId ? '/instructor' : '/organization',
    actorUserId,
    actorName,
    relatedEntityType: 'booking_request',
    relatedEntityId: bookingRequest.bookingRequestId || bookingRequest.id,
  });
}

async function notifyBookingRequester(db, type, title, message, bookingRequest = {}, actorUserId = '', actorName = '') {
  const requesterUserId = bookingRequest.requesterUserId || bookingRequest.userId || '';
  if (!requesterUserId) return null;

  return createNotification(db, {
    userId: requesterUserId,
    organizationId: bookingRequest.organizationId,
    // Pass 85.1B.3 — the booking REQUESTER is a commercial/account recipient, NOT org staff. Explicit 'commercial' audience
    // → fail-closed for the staff branch (a requester who also happens to be staff never reads it AS operations); a
    // future pass may add source-backed (backing booking) read authority.
    audience: 'commercial',
    type,
    title,
    message,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: bookingRequest.bookingId ? 'booking' : 'booking_request',
    relatedEntityId: bookingRequest.bookingId || bookingRequest.bookingRequestId || bookingRequest.id,
  });
}

export async function notifyBookingRequestAccepted(db, { bookingRequest = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_request_accepted',
    'Booking request accepted',
    'Your booking request was accepted.',
    bookingRequest,
    actorUserId,
    actorName
  );
}

export async function notifyBookingRequestDeclined(db, { bookingRequest = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_request_declined',
    'Booking request declined',
    'Your booking request was declined.',
    bookingRequest,
    actorUserId,
    actorName
  );
}

export async function notifyBookingRequestCanceled(db, { bookingRequest = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_request_canceled',
    'Booking request canceled',
    'A booking request was canceled.',
    bookingRequest,
    actorUserId,
    actorName
  );
}

export async function notifyBookingRequestCompleted(db, { bookingRequest = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_request_completed',
    'Booking completed',
    'A booking was marked complete.',
    bookingRequest,
    actorUserId,
    actorName
  );
}

export async function notifyBookingConfirmed(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_confirmed',
    'Booking confirmed',
    'Your booking is now confirmed.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingRescheduled(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_rescheduled',
    'Booking rescheduled',
    'Your booking time was updated.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingCanceled(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_canceled',
    'Booking canceled',
    'Your confirmed booking was canceled.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingCompleted(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_completed',
    'Booking completed',
    'Your booking was marked complete.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyUpcomingBookingReminder(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_upcoming_reminder',
    'Upcoming session reminder',
    'Your coaching session is coming up soon.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyPostSessionUploadPrompt(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_post_session_upload',
    'Upload session video',
    'Your session is complete. Upload video or notes so coaching can continue.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyFeedbackReadyForBooking(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_feedback_ready',
    'Session feedback ready',
    'Feedback from your session is ready to review.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyNextSessionRecommended(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_rebook_recommended',
    'Book your next session',
    'Your coach recommended booking the next session.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingNoShow(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_no_show',
    'Booking marked no-show',
    'A booking was marked as a no-show.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingFollowUpRequested(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_follow_up_requested',
    'Session follow-up requested',
    'A session follow-up is ready for you.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingChangeRequested(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  await createOrganizationNotification(db, {
    organizationId: booking.organizationId,
    roles: booking.instructorId ? ['organization_owner', 'organization_admin', 'instructor'] : ['organization_owner', 'organization_admin'],
    type: 'booking_change_requested',
    title: 'Booking change requested',
    message: `${booking.requesterName || booking.requesterEmail || 'A requester'} asked to ${booking?.requesterChangeRequest?.type || 'change'} a booking.`,
    link: booking.instructorId ? '/instructor' : '/organization',
    actorUserId,
    actorName,
    relatedEntityType: 'booking',
    relatedEntityId: booking.bookingId || booking.id,
  });

  return notifyBookingRequester(
    db,
    'booking_change_requested',
    'Booking change requested',
    'Your booking change request was submitted.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingChangeApproved(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_change_approved',
    'Booking change approved',
    'Your booking change request was approved.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingChangeDeclined(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_change_declined',
    'Booking change declined',
    'Your booking change request was declined.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingPaymentPending(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_payment_pending',
    'Booking payment pending',
    'This booking may require payment or deposit later.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingPaymentReceived(db, { booking = {}, payment = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_payment_received',
    'Payment received',
    `Payment${payment?.amount ? ` of ${payment.amount} ${payment.currency || ''}` : ''} was received for your booking.`,
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingPaymentFailed(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_payment_failed',
    'Payment failed',
    'A payment attempt for your booking failed.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingRefundIssued(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_refund_issued',
    'Refund issued',
    'A refund was issued for your booking.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyBookingCoverageApplied(db, { booking = {}, actorUserId = '', actorName = '' } = {}) {
  return notifyBookingRequester(
    db,
    'booking_coverage_applied',
    'Coverage applied',
    'Package, plan, or waiver coverage was applied to your booking.',
    booking,
    actorUserId,
    actorName
  );
}

export async function notifyPackageAssigned(db, { membership = {}, servicePackage = {}, actorUserId = '', actorName = '' } = {}) {
  if (!membership?.userId) return null;
  return createNotification(db, {
    userId: membership.userId,
    organizationId: membership.organizationId || '',
    audience: 'commercial',
    type: 'package_assigned',
    title: 'Package assigned',
    message: `${servicePackage?.name || 'A package'} is now available on your account.`,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: 'customer_membership',
    relatedEntityId: membership.customerMembershipId || membership.id || '',
  });
}

export async function notifyCreditsLow(db, { membership = {}, servicePackage = {}, actorUserId = '', actorName = '' } = {}) {
  if (!membership?.userId) return null;
  return createNotification(db, {
    userId: membership.userId,
    organizationId: membership.organizationId || '',
    audience: 'commercial',
    type: 'package_credits_low',
    title: 'Coverage running low',
    message: `${servicePackage?.name || 'Your package'} is getting low on remaining sessions or credits.`,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: 'customer_membership',
    relatedEntityId: membership.customerMembershipId || membership.id || '',
  });
}

export async function notifyMembershipExpiringSoon(db, { membership = {}, servicePackage = {}, actorUserId = '', actorName = '' } = {}) {
  if (!membership?.userId) return null;
  return createNotification(db, {
    userId: membership.userId,
    organizationId: membership.organizationId || '',
    audience: 'commercial',
    type: 'membership_expiring_soon',
    title: 'Membership expiring soon',
    message: `${servicePackage?.name || 'A package'} is approaching its end date.`,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: 'customer_membership',
    relatedEntityId: membership.customerMembershipId || membership.id || '',
  });
}

export async function notifyPackageCoverageExhausted(db, { membership = {}, servicePackage = {}, actorUserId = '', actorName = '' } = {}) {
  if (!membership?.userId) return null;
  return createNotification(db, {
    userId: membership.userId,
    organizationId: membership.organizationId || '',
    audience: 'commercial',
    type: 'package_coverage_exhausted',
    title: 'Coverage exhausted',
    message: `${servicePackage?.name || 'A package'} no longer has remaining sessions or credits.`,
    link: '/my-bookings',
    actorUserId,
    actorName,
    relatedEntityType: 'customer_membership',
    relatedEntityId: membership.customerMembershipId || membership.id || '',
  });
}
